import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPlanService, type PlanServiceCreatePayload } from '../services/plans';
import { updateService, deleteService } from '../services/smr-admin';
import { serviceKeys, serviceItemsQueryKeyRoot } from './useServicesQuery';
import { serviceKeys as salesJourneyServiceKeys } from '@/features/service-plans/hooks/useServicesQuery';

// Root of every plan-services list cache. Invalidate the root (prefix match)
// to bust the services list of every plan at once, e.g. after a service edit.
export const planServicesQueryKeyRoot = ['smr-admin', 'plan-services'] as const;

export const planServicesQueryKey = (planId: string) => [...planServicesQueryKeyRoot, planId];

export function usePlanServiceMutations(planId: string) {
    const queryClient = useQueryClient();

    // A create, update or remove can shift sibling positions, touches the shared
    // catalogue row, and must not survive in the sales-journey cache. The promise is
    // returned so the mutation stays pending until every refetch has reconciled.
    const invalidateScheduleCaches = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: planServicesQueryKeyRoot }),
            queryClient.invalidateQueries({ queryKey: serviceKeys.all }),
            queryClient.invalidateQueries({ queryKey: salesJourneyServiceKeys.all }),
            queryClient.invalidateQueries({ queryKey: serviceItemsQueryKeyRoot }),
        ]);

    return {
        createMutation: useMutation({
            mutationFn: (payload: PlanServiceCreatePayload) => createPlanService(planId, payload),
            onSuccess: invalidateScheduleCaches,
        }),
        updateMutation: useMutation({
            mutationFn: ({ serviceId, payload }: { serviceId: string; payload: PlanServiceCreatePayload }) =>
                updateService(serviceId, payload),
            onSuccess: invalidateScheduleCaches,
        }),
        removeMutation: useMutation({
            mutationFn: (serviceId: string) => deleteService(serviceId),
            onSuccess: invalidateScheduleCaches,
        }),
    };
}
